import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Button, Input, message, Spin, Upload } from "antd";
import { toast } from "react-toastify";
import styled from "styled-components";
import { authenticatedCompany } from "../helper/AuthHelper";
import { reduxFunctions } from "../helper/ReduxHelper";
import { connect } from "react-redux";
import { DOCUMENT_TYPES, VERIFICATION_STATUSES } from "../constants/Enums";
import loadingIcon from "./common/loadingIcon";
import { useHistory } from "react-router-dom";

const { Dragger } = Upload;

const initialState = {
  document_type: DOCUMENT_TYPES.CAC,
  document_id: "",
  document_image: "",
};

const inputStyle = {
  border: "1px solid #d0d5dd",
  borderRadius: "8px",
  fontSize: "14px",
};
const uploadTextStyle = { color: "#e27626", fontSize: "14px" };
const uploadHintStyle = { fontSize: "12px" };
const submitWrapperStyle = { marginTop: "20px" };

const instructions = [
  "Please note that only verified companies will be able to access our services.",
  "Our team will review your CAC document and get back to you within 24-48 hours.",
  "If you have any questions or concerns, please contact our customer support team.",
];

const DocumentVerificationModal = ({
  getUploadUrl,
  updateCompany,
  getCompany,
}) => {
  const [kycData, setKycData] = useState(initialState);
  const [loading, setLoading] = useState(false);

  const uploadUrlRef = useRef(null);
  const s3ImageUrlRef = useRef(null);

  const [company, setCompany] = useState(() => authenticatedCompany());
  const navigate = useHistory();

  const cac_document = company?.documents?.find(
    (el) => el.document_type === DOCUMENT_TYPES.CAC,
  );
  const isInReview = cac_document?.status === VERIFICATION_STATUSES.IN_REVIEW;

  const beforeUpload = useCallback(
    async (file) => {
      const isValidType =
        file.type === "image/jpeg" ||
        file.type === "image/png" ||
        file.type === "application/pdf";
      if (!isValidType) {
        message.error("You can only upload an acceptable file");
        return Upload.LIST_IGNORE;
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        toast.error("Image should not exceed 2MB");
        return Upload.LIST_IGNORE;
      }
      try {
        const res = await getUploadUrl(file.type);
        uploadUrlRef.current = res.payload.data.upload.url;
        s3ImageUrlRef.current = res.payload.data.upload.path;
        return true;
      } catch {
        message.error("Failed to prepare upload");
        return Upload.LIST_IGNORE;
      }
    },
    [getUploadUrl],
  );

  const handleUpload = useCallback(({ file, onSuccess, onProgress }) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrlRef.current);
    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          setKycData((prev) => ({
            ...prev,
            document_image: s3ImageUrlRef.current,
          }));
          onSuccess(null, file);
        } else {
          message.error("Upload Failed");
        }
      }
    };
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onProgress({ percent: Math.round((e.loaded / e.total) * 100) }, file);
      }
    };
    xhr.send(file);
  }, []);

  const draggerProps = useMemo(
    () => ({
      name: "file",
      beforeUpload,
      multiple: false,
      maxCount: 1,
      customRequest: handleUpload,
    }),
    [beforeUpload, handleUpload],
  );

  const handleDocumentIdChange = useCallback((e) => {
    const value = e.target.value;
    setKycData((prev) => ({ ...prev, document_id: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (
        !kycData?.document_id ||
        !kycData?.document_image ||
        !kycData?.document_type
      )
        return toast.error("All fields are required");

      const payload = {
        ...company,
        documents: [...(company?.documents || []), kycData],
      };

      setLoading(true);
      try {
        await updateCompany(payload);
        const res = await getCompany();
        const updatedCompany = { ...res?.payload?.data?.companies[0] };
        localStorage.setItem("company", JSON.stringify(updatedCompany));
        setCompany(updatedCompany);
        toast.success("Document submitted successfully");
      } catch {
        toast.error("Error submitting document");
      } finally {
        setLoading(false);
      }
    },
    [kycData, company, updateCompany, getCompany],
  );

  useEffect(() => {
    if (!isInReview) return;
    getCompany()
      .then((res) => {
        const userCompany = { ...res?.payload?.data?.companies[0] };
        localStorage.setItem("company", JSON.stringify(userCompany));
        setCompany(userCompany);
      })
      .catch(() => {});
  }, [isInReview, getCompany]);

  const handleLogout = useCallback(() => navigate.push("/logout"), [navigate]);

  return (
    <Container>
      {isInReview ? (
        <div className="content-container">
          <img src="/verification-icon.svg" alt="KYC" />
          <h2>Verification in Progress</h2>

          <div className="instruction-container">
            {instructions.map((instruction, i) => (
              <ul key={i}>
                <li>{instruction}</li>
              </ul>
            ))}
          </div>

          <span className="logout" onClick={handleLogout}>
            Log Out
          </span>
        </div>
      ) : (
        <div className="content-container">
          <img src="/kyc-icon.svg" alt="KYC" />
          <h2>Company CAC Document</h2>
          <p>
            Please upload a copy of your company CAC document for verification
          </p>

          <form className="form-container">
            <div className="input-section">
              <h2 className="sub-title">Company Registration Number</h2>
              <Input
                name="id"
                value={kycData.document_id}
                onChange={handleDocumentIdChange}
                size="large"
                style={inputStyle}
              />
            </div>

            <div className="input-section">
              <h2 className="sub-title">Upload CAC Document</h2>
              <Dragger {...draggerProps}>
                <p className="ant-upload-text">
                  <span style={uploadTextStyle}>Click or upload</span> or drag
                  and drop
                </p>
                <p className="ant-upload-hint" style={uploadHintStyle}>
                  PNG, JPG or PDF(max 2MB)
                </p>
              </Dragger>
            </div>

            <div style={submitWrapperStyle}>
              <Button htmlType="submit" type="primary" onClick={handleSubmit}>
                {loading ? <Spin indicator={loadingIcon} /> : "Submit"}
              </Button>
            </div>

            <span className="logout" onClick={handleLogout}>
              Log Out
            </span>
          </form>
        </div>
      )}
    </Container>
  );
};

const Container = styled.div`
  min-height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 20px;

  .content-container {
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
  }

  .instruction-container {
    display: flex;
    flex-direction: column;
    gap: 6px;

    ul {
      list-style-position: inside;
      margin: 0;
    }

    li {
      color: #8a94a6;
    }
  }

  .form-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .select-container {
    border: 1px solid #d0d5dd;
    border-radius: 8px;
    width: 100%;
  }

  .input-section {
    text-align: left;
    font-size: 16px;

    select {
      width: 100%;
    }
  }

  .sub-title {
    color: #344054;
    font-weight: 500;
    font-size: 14px;
    margin-bottom: 10px;
  }

  img {
    max-height: 80px;
    width: 80px;
    margin: 0 auto;
  }

  h2 {
    margin: 5px 0 0px 0;
    color: #000000;
    padding: 0;
  }

  p {
    margin: 3px 0;
    color: #667085;
  }

  button {
    width: 100%;
    height: 42px;
    border-radius: 5px;
  }

  .logout {
    text-align: right;
    color: #e27626;
    cursor: pointer;
  }
`;

const mapStateToProps = ({ user }) => ({ user });

const mapDispatchToProps = reduxFunctions;

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(DocumentVerificationModal);
